import Legend from 'components/Legend/Legend';
import './PitcherSingleSessionPitchTypesUsageSummaryChart.scss';

import {
  PitcherSingleSessionPitchTypesUsageSummaryChartConfigDto,
  PitcherSingleSessionPitchTypesUsageSummaryChartDataDto,
} from 'types/CodegenReportTypes';

import { ChartProps } from 'utilities/chartIdToComponent';
import { pitchTypeGroupToColor, pitchTypeToColor } from 'utilities/colorHelpers';
import { useCallback, useMemo } from 'react';
import { useChartDataDisplay } from 'hooks/helperHooks';
import { Tooltip, Typography } from '@trackman/web-shared-components';
import { useTranslation } from 'react-i18next';
import { pitchTypeGroupLegendItems, pitchTypeGroups, pitchTypeLegendItems, pitchTypes } from 'utilities/constants';
import useReportStore from 'hooks/SessionReport/PitcherSingle/reportStore';

const PitcherSingleSessionPitchTypesUsageSummaryChart = ({
  chartData,
  isLoading,
  extraChartData,
}: ChartProps<PitcherSingleSessionPitchTypesUsageSummaryChartDataDto, PitcherSingleSessionPitchTypesUsageSummaryChartConfigDto>) => {
  const { t } = useTranslation();
  const { selectedConfgValues } = useReportStore();

  const simulateChartData = useCallback((isLoading: boolean) => {
    const simulatedChartData: PitcherSingleSessionPitchTypesUsageSummaryChartDataDto = {
      chartId: 'PitcherSingleSessionPitchTypesUsageSummaryChart',
      total: 0,
      pitchUsages: [],
    };

    if (isLoading) {
      const pitchTypeLabels = selectedConfgValues.pitchGrouping === 'Types' ? pitchTypes : pitchTypeGroups;
      for (const pitchType of pitchTypeLabels.filter((pitchType) => pitchType !== 'Undefined')) {
        const count = Math.floor(Math.random() * 9) + 2;
        simulatedChartData.pitchUsages.push({ pitchTypeOrGroup: pitchType, count });
        simulatedChartData.total += count;
      }
    }

    return simulatedChartData;
  }, []);

  const chartDataDisplay = useChartDataDisplay(chartData, simulateChartData, isLoading);

  const sortedPitchUsages = useMemo(
    () => chartDataDisplay.pitchUsages.sort((a, b) => a.pitchTypeOrGroup.localeCompare(b.pitchTypeOrGroup)),
    [chartDataDisplay.pitchUsages]
  );

  const filteredLegendItems = useMemo(
    () =>
      (selectedConfgValues.pitchGrouping === 'Types' ? pitchTypeLegendItems : pitchTypeGroupLegendItems).filter((item) =>
        chartDataDisplay.pitchUsages.map((x) => x.pitchTypeOrGroup).includes(item.label)
      ),
    [chartDataDisplay]
  );

  return (
    <div className='PitcherSingleSessionPitchTypesUsageSummaryChart-container'>
      <div className='PitcherSingleSessionPitchTypesUsageSummaryChart-bar-wrapper'>
        <div className='PitcherSingleSessionPitchTypesUsageSummaryChart-bar'>
          {sortedPitchUsages.map((usage) => (
            <Tooltip key={usage.pitchTypeOrGroup} title={`${t(usage.pitchTypeOrGroup)}: ${usage.count}`}>
              <div
                className='PitcherSingleSessionPitchTypesUsageSummaryChart-bar-item'
                style={{
                  width: `${(usage.count / chartDataDisplay.total) * 100}%`,
                  backgroundColor: `${pitchTypeToColor[usage.pitchTypeOrGroup] ?? pitchTypeGroupToColor[usage.pitchTypeOrGroup]}80`,
                  borderColor: pitchTypeToColor[usage.pitchTypeOrGroup] ?? pitchTypeGroupToColor[usage.pitchTypeOrGroup],
                }}
              />
            </Tooltip>
          ))}
        </div>
        <div className='PitcherSingleSessionPitchTypesUsageSummaryChart-total-wrapper'>
          <Typography variant='body' component='div'>
            {t('Total')}:
          </Typography>
          <Typography variant='h1' component='div' className='PitcherSingleSessionPitchTypesUsageSummaryChart-total-value'>
            {chartDataDisplay.total || '-'}
          </Typography>
        </div>
      </div>
      <Legend items={filteredLegendItems} />
    </div>
  );
};

export default PitcherSingleSessionPitchTypesUsageSummaryChart;
